import {
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragOverEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { useCallback, useState } from 'react';
import type { TaskType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import type { TaskCardOpenIntent } from '../../../TaskCard';
import {
    moveWithinColumn,
    onStatusChanged,
    type KanbanColumnOrder,
} from '../helpers/kanbanColumnOrder';
import { parseColumnDroppableId, resolveDropAction } from '../helpers/kanbanDropRules';
import { findTaskColumnStatus } from './boardHelpers';
import type { KanbanColumnView } from './useKanbanBoard';
import { kanbanCollisionDetection } from './kanbanCollisionDetection';

export interface UseKanbanTaskMoveParams {
    userId: number;
    columns: KanbanColumnView[];
    taskById: Map<number, TaskType>;
    columnOrder: KanbanColumnOrder;
    persistOrder: (next: KanbanColumnOrder) => void;
    triggerListRefresh: () => void;
    onOpenWithIntent: (taskId: number, intent: TaskCardOpenIntent) => void;
}

const useKanbanTaskMove = ({
    userId,
    columns,
    taskById,
    columnOrder,
    persistOrder,
    triggerListRefresh,
    onOpenWithIntent,
}: UseKanbanTaskMoveParams) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const params = useSettingsParams();

    const [activeTask, setActiveTask] = useState<TaskType | null>(null);
    const [overColumnStatus, setOverColumnStatus] = useState<number | null>(null);
    const [statusUpdating, setStatusUpdating] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );

    const resolveOverStatus = useCallback(
        (overId: string | number | undefined): number | null => {
            if (overId == null) return null;
            const asColumn = parseColumnDroppableId(overId);
            if (asColumn != null) return asColumn;
            const asTaskId = Number(overId);
            if (!Number.isFinite(asTaskId)) return null;
            return findTaskColumnStatus(columns, asTaskId);
        },
        [columns],
    );

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            const task = taskById.get(Number(event.active.id)) || null;
            setActiveTask(task);
            if (task) setOverColumnStatus(Number(task.taskStatus));
        },
        [taskById],
    );

    const handleDragOver = useCallback(
        (event: DragOverEvent) => {
            setOverColumnStatus(resolveOverStatus(event.over?.id));
        },
        [resolveOverStatus],
    );

    const handleDragCancel = useCallback(() => {
        setActiveTask(null);
        setOverColumnStatus(null);
    }, []);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            setActiveTask(null);
            setOverColumnStatus(null);

            if (!over) return;

            const activeId = Number(active.id);
            const task = taskById.get(activeId);
            if (!task) return;

            const fromStatus = Number(task.taskStatus);
            const toStatus = resolveOverStatus(over.id);
            if (toStatus == null) return;

            const action = resolveDropAction(task, fromStatus, toStatus, userId, {
                autoAcceptOwn: params.tasksAutoAcceptOwn,
            });

            if (action.type === 'deny') {
                showToast(t(action.toastKey), 'warning');
                return;
            }

            if (action.type === 'reorder') {
                const overId = Number(over.id);
                if (!Number.isFinite(overId) || overId === activeId) {
                    const col = columns.find((c) => c.status === fromStatus);
                    if (col) {
                        const ids = col.tasks.map((item) => item.ID);
                        persistOrder({ ...columnOrder, [String(fromStatus)]: ids });
                    }
                    return;
                }
                const col = columns.find((c) => c.status === fromStatus);
                const columnTaskIds = col?.tasks.map((item) => item.ID) || [];
                persistOrder(
                    moveWithinColumn(columnOrder, fromStatus, activeId, overId, columnTaskIds),
                );
                return;
            }

            if (action.type === 'openResponse') {
                showToast(t(action.toastKey), 'info');
                onOpenWithIntent(activeId, 'response');
                return;
            }

            if (action.type === 'openReject') {
                showToast(t(action.toastKey), 'info');
                onOpenWithIntent(activeId, 'reject');
                return;
            }

            if (action.type === 'status') {
                setStatusUpdating(true);
                const result = await apiTask.updateTaskStatus(activeId, action.status);
                setStatusUpdating(false);
                if (!result?.stat) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
                showToast(t(action.toastKey), 'success');
                persistOrder(onStatusChanged(columnOrder, activeId, fromStatus, action.status));
                triggerListRefresh();
            }
        },
        [
            taskById,
            resolveOverStatus,
            userId,
            params.tasksAutoAcceptOwn,
            showToast,
            t,
            columns,
            columnOrder,
            persistOrder,
            onOpenWithIntent,
            triggerListRefresh,
        ],
    );

    return {
        sensors,
        collisionDetection: kanbanCollisionDetection,
        activeTask,
        overColumnStatus,
        statusUpdating,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
    };
};

export default useKanbanTaskMove;
